import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { toast } from 'react-hot-toast';
import { Car, Phone, Calendar, MapPin, CheckCircle, AlertCircle, User, Hash, Users, FileText, Clock, Upload, X, ArrowLeft } from 'lucide-react';
import api from '../../config/api';

const VehicleRegistrationForm = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    vehicleType: '',
    licensePlate: '',
    make: '',
    model: '',
    year: '',
    pincode: '',
    state: '',
    city: '',
    capacity: '',
    availabilityDate: new Date().toISOString().split('T')[0],
    availableDays: '',
    isCurrentlyAvailable: false,
    additionalNotes: '',
    vehicleImage: ''
  });
  const [states, setStates] = useState([]);
  const [cities, setCities] = useState({});
  const [loading, setLoading] = useState(false);
  const [imagePreview, setImagePreview] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [registeredVehicle, setRegisteredVehicle] = useState(null);
  const [currentStep, setCurrentStep] = useState(1);
  const totalSteps = 3;

  // Check if user is logged in and fetch states/cities
  useEffect(() => {
    const token = localStorage.getItem('vehicleToken');
    if (!token) {
      toast.error('Please log in to register a vehicle');
      navigate('/vehicle/login');
      return;
    }

    // Fetch states and cities
    const fetchStatesAndCities = async () => {
      try {
        setLoading(true);
        const response = await api.get('/location/states');
        if (response.data.success) {
          const stateData = response.data.data;
          // Extract state names
          const stateNames = Object.keys(stateData);
          setStates(stateNames);

          // Store city data
          setCities(stateData);
        } else {
          toast.error('Failed to load location data');
        }
      } catch (error) {
        console.error('Error fetching location data:', error);
        toast.error('Failed to load location data. Please try again later.');
      } finally {
        setLoading(false);
      }
    };

    fetchStatesAndCities();
  }, [navigate]);

  const vehicleTypes = [
    { value: 'car', label: 'Car', icon: '🚗' },
    { value: 'ambulance', label: 'Ambulance', icon: '🚑' },
    { value: 'van', label: 'Van', icon: '🚐' },
    { value: 'other', label: 'Other', icon: '🚙' }
  ];

  const handleSubmit = async (e) => {
    e.preventDefault();
    setErrorMessage('');
    console.log('Form submission started');

    // Validate all required fields
    const requiredFields = [
      'vehicleType',
      'licensePlate',
      'pincode',
      'state',
      'city',
      'capacity',
      'availabilityDate',
      'availableDays'
    ];

    const missingFields = requiredFields.filter(field => !formData[field]);

    if (missingFields.length > 0) {
      const missingFieldNames = missingFields.join(', ');
      setErrorMessage(`Please complete these required fields: ${missingFieldNames}`);
      toast.error('Please complete all required fields before submitting');
      return;
    }

    setIsSubmitting(true);

    try {
      // Prepare data for backend - ensure proper data types
      const vehicleData = {
        ...formData,
        capacity: parseInt(formData.capacity),
        availableDays: parseInt(formData.availableDays),
        isCurrentlyAvailable: formData.isCurrentlyAvailable || false,
        additionalNotes: formData.additionalNotes || ''
      };

      console.log('Sending vehicle data to backend:', vehicleData);

      // Get the token from localStorage
      const token = localStorage.getItem('vehicleToken');

      // Send data to backend API with authentication
      const response = await api.post('/vehicle/user/register-vehicle', vehicleData, {
        headers: {
          'Content-Type': 'application/json',
          'x-auth-token': token
        }
      });

      console.log('Response from backend:', response.data);

      toast.success('Vehicle registered successfully!');

      // Set success message and store registered vehicle details
      setSuccessMessage('Your vehicle has been registered successfully! It is now pending approval.');
      setRegisteredVehicle(response.data.vehicle);

      // Reset form
      setFormData({
        vehicleType: '',
        licensePlate: '',
        make: '',
        model: '',
        year: '',
        pincode: '',
        state: '',
        city: '',
        capacity: '',
        availabilityDate: '',
        availableDays: '',
        isCurrentlyAvailable: false,
        additionalNotes: '',
        vehicleImage: ''
      });
      setImagePreview(null);
      setErrorMessage('');

      // Scroll to top to show success message
      window.scrollTo({ top: 0, behavior: 'smooth' });

      // Redirect to dashboard after a delay
      setTimeout(() => {
        navigate('/vehicle/dashboard');
      }, 3000);
    } catch (error) {
      console.error('Error registering vehicle:', error);
      console.error('Error details:', error.response?.data);

      const errorMsg = error.response?.data?.message || error.message || 'Failed to register vehicle. Please try again.';
      setErrorMessage(errorMsg);
      setSuccessMessage('');
      setRegisteredVehicle(null);

      toast.error(errorMsg);
    } finally {
      setIsSubmitting(false);
    }
  };

  const nextStep = () => {
    if (currentStep < totalSteps) {
      setCurrentStep(currentStep + 1);
    }
  };

  const prevStep = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  };

  const renderStepIndicator = () => (
    <div className="mb-6">
      <div className="flex items-center justify-center space-x-3">
        {[1, 2, 3].map((step) => (
          <div key={step} className="flex items-center">
            <div className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-medium transition-colors duration-200 ${step <= currentStep
              ? 'bg-red-600 text-white shadow-md'
              : 'bg-gray-200 text-gray-600'
              }`}>
              {step}
            </div>
            {step < 3 && (
              <div className={`w-12 h-1 mx-2 rounded-full transition-colors duration-200 ${step < currentStep ? 'bg-red-600' : 'bg-gray-200'
                }`} />
            )}
          </div>
        ))}
      </div>
      <div className="text-center mt-3">
        <p className="text-xs font-semibold text-gray-700">
          Step {currentStep}: {
            currentStep === 1 ? 'Vehicle Information' :
              currentStep === 2 ? 'Location & Availability' :
                'Additional Details'
          }
        </p>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-gradient-to-br from-red-50 via-white to-blue-50">
      {/* Header */}
      <div className="bg-white shadow-sm border-b">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <button
                onClick={() => navigate('/vehicle/dashboard')}
                className="p-1.5 rounded-lg hover:bg-gray-100 transition-colors"
              >
                <ArrowLeft className="h-4 w-4 text-gray-600" />
              </button>
              <div>
                <h1 className="text-xl font-bold text-gray-900">Vehicle Registration</h1>
                <p className="text-xs text-gray-600">Join our emergency transportation network</p>
              </div>
            </div>
            <div className="hidden md:flex items-center space-x-2 text-xs text-gray-500">
              <Car className="h-3.5 w-3.5" />
              <span>BloodHero Vehicle Network</span>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="space-y-8"
        >
          {/* Success/Error Messages */}
          {successMessage && (
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              className="bg-green-50 border border-green-200 rounded-xl p-6"
            >
              <div className="flex items-start">
                <CheckCircle className="text-green-500 mr-3 mt-1 flex-shrink-0 h-6 w-6" />
                <div className="flex-1">
                  <h3 className="font-semibold text-green-800 text-lg">Registration Successful!</h3>
                  <p className="text-green-700 mt-1">{successMessage}</p>
                  {registeredVehicle && (
                    <div className="mt-4 p-4 bg-white rounded-lg border border-green-100">
                      <div className="grid grid-cols-2 gap-4 text-sm">
                        <div>
                          <span className="font-medium text-gray-700">Vehicle Type:</span>
                          <span className="ml-2 text-gray-900">{registeredVehicle.vehicleType}</span>
                        </div>
                        <div>
                          <span className="font-medium text-gray-700">License Plate:</span>
                          <span className="ml-2 text-gray-900">{registeredVehicle.licensePlate}</span>
                        </div>
                        <div>
                          <span className="font-medium text-gray-700">Status:</span>
                          <span className="ml-2 px-2 py-1 bg-yellow-100 text-yellow-800 rounded-full text-xs">
                            {registeredVehicle.status}
                          </span>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </motion.div>
          )}

          {errorMessage && (
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              className="bg-red-50 border border-red-200 rounded-lg p-3 sm:p-4 mb-4"
            >
              <div className="flex items-start">
                <AlertCircle className="text-red-500 mr-2 mt-0.5 flex-shrink-0 h-4 w-4" />
                <div className="text-sm text-red-700">{errorMessage}</div>
              </div>
            </motion.div>
          )}

          {/* Step Indicator */}
          {renderStepIndicator()}

          {/* Form Container */}
          <div className="bg-white rounded-2xl shadow-lg border border-gray-100 overflow-hidden">
            <form onSubmit={handleSubmit} className="p-6 sm:p-8">
              {/* Step 1: Vehicle Information */}
              {currentStep === 1 && (
                <motion.div
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  className="space-y-8"
                >
                  <div className="text-center mb-6">
                    <div className="inline-flex items-center justify-center w-12 h-12 bg-red-100 rounded-full mb-3">
                      <Car className="h-6 w-6 text-red-600" />
                    </div>
                    <h2 className="text-xl font-bold text-gray-900 mb-1">Vehicle Information</h2>
                    <p className="text-xs text-gray-600">Tell us about your vehicle</p>
                  </div>

                  {/* Vehicle Type Selection */}
                  <div className="space-y-3">
                    <label className="block text-xs font-semibold text-gray-700 mb-2">
                      Vehicle Type <span className="text-red-500">*</span>
                    </label>
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                      {vehicleTypes.map((type) => (
                        <button
                          key={type.value}
                          type="button"
                          onClick={() => setFormData({ ...formData, vehicleType: type.value })}
                          className={`p-3 rounded-xl border transition-all duration-200 flex flex-col items-center justify-center ${formData.vehicleType === type.value
                            ? 'border-red-500 bg-red-50 text-red-700 shadow-sm'
                            : 'border-gray-200 hover:border-red-200 hover:bg-gray-50 bg-white'
                            }`}
                        >
                          <div className="text-xl mb-1">{type.icon}</div>
                          <div className="text-xs font-medium">{type.label}</div>
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Vehicle Details */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-1.5">
                      <label className="block text-xs font-semibold text-gray-700">
                        License Plate <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="text"
                        className="w-full px-3 py-2.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-red-500 focus:border-red-500 transition-all duration-200"
                        value={formData.licensePlate}
                        onChange={(e) => setFormData({ ...formData, licensePlate: e.target.value })}
                        placeholder="Enter license plate number"
                        required
                      />
                    </div>

                    <div className="space-y-1.5">
                      <label className="block text-xs font-semibold text-gray-700">
                        Passenger Capacity <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="number"
                        min="1"
                        max="6"
                        className="w-full px-3 py-2.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-red-500 focus:border-red-500 transition-all duration-200"
                        value={formData.capacity}
                        onChange={(e) => {
                          let value = parseInt(e.target.value);
                          if (value > 6) value = 6;
                          if (value < 1) value = '';
                          setFormData({ ...formData, capacity: value });
                        }}
                        placeholder="Number of passengers"
                        required
                      />
                      <p className="text-[10px] text-gray-500">Maximum capacity: 6 passengers</p>
                    </div>
                  </div>

                  <div className="grid md:grid-cols-3 gap-6">
                    <div className="space-y-2">
                      <label className="block text-sm font-semibold text-gray-700">Make</label>
                      <input
                        type="text"
                        className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-red-500 focus:border-red-500 transition-all duration-200"
                        value={formData.make}
                        onChange={(e) => setFormData({ ...formData, make: e.target.value })}
                        placeholder="e.g., Toyota, Honda"
                      />
                    </div>

                    <div className="space-y-2">
                      <label className="block text-sm font-semibold text-gray-700">Model</label>
                      <input
                        type="text"
                        className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-red-500 focus:border-red-500 transition-all duration-200"
                        value={formData.model}
                        onChange={(e) => setFormData({ ...formData, model: e.target.value })}
                        placeholder="e.g., Camry, Civic"
                      />
                    </div>

                    <div className="space-y-2">
                      <label className="block text-sm font-semibold text-gray-700">Year</label>
                      <input
                        type="number"
                        min="1900"
                        max={new Date().getFullYear()}
                        className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-red-500 focus:border-red-500 transition-all duration-200"
                        value={formData.year}
                        onChange={(e) => {
                          const val = e.target.value;
                          if (val.length <= 4) {
                            setFormData({ ...formData, year: val });
                          }
                        }}
                        placeholder="e.g., 2020"
                      />
                    </div>
                  </div>
                </motion.div>
              )}

              {/* Step 2: Location & Availability */}
              {currentStep === 2 && (
                <motion.div
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  className="space-y-8"
                >
                  <div className="text-center mb-8">
                    <div className="inline-flex items-center justify-center w-16 h-16 bg-blue-100 rounded-full mb-4">
                      <MapPin className="h-8 w-8 text-blue-600" />
                    </div>
                    <h2 className="text-2xl font-bold text-gray-900 mb-2">Location & Availability</h2>
                    <p className="text-gray-600">Where and when can you help?</p>
                  </div>

                  {/* Location */}
                  <div className="grid md:grid-cols-3 gap-6">
                    <div className="space-y-2">
                      <label className="block text-sm font-semibold text-gray-700">
                        State <span className="text-red-500">*</span>
                      </label>
                      <select
                        className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-red-500 focus:border-red-500 transition-all duration-200 bg-white"
                        value={formData.state}
                        onChange={(e) => {
                          setFormData({
                            ...formData,
                            state: e.target.value,
                            city: ''
                          });
                        }}
                        required
                        disabled={loading}
                      >
                        <option value="">Select State</option>
                        {states.map(state => (
                          <option key={state} value={state}>{state}</option>
                        ))}
                      </select>
                    </div>

                    <div className="space-y-2">
                      <label className="block text-sm font-semibold text-gray-700">
                        City <span className="text-red-500">*</span>
                      </label>
                      <select
                        className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-red-500 focus:border-red-500 transition-all duration-200 bg-white"
                        value={formData.city}
                        onChange={(e) => setFormData({ ...formData, city: e.target.value })}
                        required
                        disabled={!formData.state || loading}
                      >
                        <option value="">Select City</option>
                        {formData.state && cities[formData.state]?.map(city => (
                          <option key={city} value={city}>{city}</option>
                        ))}
                      </select>
                    </div>

                    <div className="space-y-2">
                      <label className="block text-sm font-semibold text-gray-700">
                        Pincode <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="text"
                        className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-red-500 focus:border-red-500 transition-all duration-200"
                        value={formData.pincode}
                        onChange={(e) => setFormData({ ...formData, pincode: e.target.value })}
                        placeholder="Enter pincode"
                        required
                      />
                    </div>
                  </div>

                  {/* Availability */}
                  <div className="grid md:grid-cols-2 gap-6">
                    <div className="space-y-2">
                      <label className="block text-sm font-semibold text-gray-700">
                        Available From <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="date"
                        className="w-full px-4 py-3 border border-gray-300 rounded-xl bg-gray-50 text-gray-700 cursor-not-allowed"
                        value={new Date().toISOString().split('T')[0]}
                        readOnly
                        required
                      />
                      <p className="text-xs text-gray-500">Fixed to current date</p>
                    </div>

                    <div className="space-y-2">
                      <label className="block text-sm font-semibold text-gray-700">
                        Duration <span className="text-red-500">*</span>
                      </label>
                      <select
                        className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-red-500 focus:border-red-500 transition-all duration-200 bg-white"
                        value={formData.availableDays}
                        onChange={(e) => {
                          const value = e.target.value === '24/7' ? '365' : e.target.value;
                          setFormData({ ...formData, availableDays: value });
                        }}
                        required
                      >
                        <option value="">Select Duration</option>
                        <option value="24/7">24/7 (Always Available)</option>
                        <option value="7">7 Days</option>
                        <option value="14">14 Days</option>
                        <option value="30">30 Days</option>
                        <option value="90">3 Months</option>
                        <option value="180">6 Months</option>
                        <option value="365">1 Year</option>
                        <option value="custom">Custom</option>
                      </select>
                      {formData.availableDays === 'custom' && (
                        <input
                          type="number"
                          min="1"
                          className="w-full mt-2 px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-red-500 focus:border-red-500 transition-all duration-200"
                          onChange={(e) => setFormData({ ...formData, availableDays: e.target.value })}
                          placeholder="Enter number of days"
                          required
                        />
                      )}
                    </div>
                  </div>

                  <div className="flex items-center p-4 bg-gray-50 rounded-xl">
                    <input
                      type="checkbox"
                      id="isCurrentlyAvailable"
                      className="h-5 w-5 text-red-600 focus:ring-red-500 border-gray-300 rounded"
                      checked={formData.isCurrentlyAvailable}
                      onChange={(e) => setFormData({ ...formData, isCurrentlyAvailable: e.target.checked })}
                    />
                    <label htmlFor="isCurrentlyAvailable" className="ml-3 block text-gray-700 font-medium">
                      Currently Available for Emergency Services
                    </label>
                  </div>
                </motion.div>
              )}

              {/* Step 3: Additional Details */}
              {currentStep === 3 && (
                <motion.div
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  className="space-y-8"
                >
                  <div className="text-center mb-8">
                    <div className="inline-flex items-center justify-center w-16 h-16 bg-green-100 rounded-full mb-4">
                      <FileText className="h-8 w-8 text-green-600" />
                    </div>
                    <h2 className="text-2xl font-bold text-gray-900 mb-2">Additional Details</h2>
                    <p className="text-gray-600">Any additional information to help us serve better</p>
                  </div>

                  {/* Additional Notes */}
                  <div className="space-y-2">
                    <label className="block text-sm font-semibold text-gray-700">
                      Additional Notes
                    </label>
                    <textarea
                      className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-red-500 focus:border-red-500 transition-all duration-200"
                      rows="4"
                      value={formData.additionalNotes}
                      onChange={(e) => setFormData({ ...formData, additionalNotes: e.target.value })}
                      placeholder="Any special requirements, vehicle features, or additional information that might be helpful..."
                    />
                  </div>

                  {/* Vehicle Image Upload */}
                  <div className="space-y-3">
                    <label className="block text-xs font-semibold text-gray-700">
                      Vehicle Image
                    </label>
                    <div className="border border-dashed border-gray-300 rounded-xl p-6 text-center hover:border-red-400 transition-colors bg-gray-50">
                      <input
                        type="file"
                        accept="image/*"
                        className="hidden"
                        id="vehicle-image"
                        onChange={(e) => {
                          const file = e.target.files[0];
                          if (file) {
                            const reader = new FileReader();
                            reader.onloadend = () => {
                              setFormData({ ...formData, vehicleImage: reader.result });
                              setImagePreview(reader.result);
                            };
                            reader.readAsDataURL(file);
                          }
                        }}
                      />
                      <label htmlFor="vehicle-image" className="cursor-pointer">
                        {imagePreview ? (
                          <div className="space-y-4">
                            <div className="relative inline-block">
                              <img
                                src={imagePreview}
                                alt="Vehicle preview"
                                className="w-32 h-32 object-cover rounded-lg border"
                              />
                              <button
                                type="button"
                                onClick={(e) => {
                                  e.preventDefault();
                                  setFormData({ ...formData, vehicleImage: '' });
                                  setImagePreview(null);
                                }}
                                className="absolute -top-2 -right-2 bg-red-600 text-white rounded-full p-1 hover:bg-red-700 transition-colors"
                              >
                                <X className="h-4 w-4" />
                              </button>
                            </div>
                            <p className="text-sm text-gray-600">Click to change image</p>
                          </div>
                        ) : (
                          <div className="space-y-4">
                            <Upload className="mx-auto h-12 w-12 text-gray-400" />
                            <div>
                              <p className="text-lg font-medium text-gray-700">Upload Vehicle Image</p>
                              <p className="text-sm text-gray-500">PNG, JPG, GIF up to 10MB</p>
                            </div>
                          </div>
                        )}
                      </label>
                    </div>
                    <p className="text-xs text-gray-500 text-center">
                      Upload a clear image of your vehicle to help with identification during emergencies
                    </p>
                  </div>
                </motion.div>
              )}

              {/* Navigation Buttons */}
              <div className="flex justify-between pt-8 border-t border-gray-100">
                <button
                  type="button"
                  onClick={prevStep}
                  disabled={currentStep === 1}
                  className="px-6 py-3 border border-gray-300 text-gray-700 rounded-xl hover:bg-gray-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Previous
                </button>

                {currentStep < totalSteps ? (
                  <button
                    type="button"
                    onClick={nextStep}
                    className="px-6 py-3 bg-red-600 text-white rounded-xl hover:bg-red-700 transition-colors"
                  >
                    Next
                  </button>
                ) : (
                  <motion.button
                    type="submit"
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    disabled={isSubmitting}
                    className="px-8 py-3 bg-gradient-to-r from-red-600 to-red-500 text-white font-semibold rounded-xl hover:shadow-lg transition-all duration-200 disabled:opacity-70 flex items-center space-x-2"
                  >
                    {isSubmitting ? (
                      <>
                        <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                        </svg>
                        <span>Registering...</span>
                      </>
                    ) : (
                      <>
                        <CheckCircle className="h-5 w-5" />
                        <span>Register Vehicle</span>
                      </>
                    )}
                  </motion.button>
                )}
              </div>
            </form>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default VehicleRegistrationForm;
